import * as React from 'react';
import { namespacedClassnames, isPromise } from '../../../../common';
import { Text } from '../../typography';
import { InlineControl } from '../common';
import { keyCodes, classNames } from '@wix/answers-lib';
import TextAreaDefault from 'react-textarea-autosize';
// tslint:disable-next-line:no-duplicate-imports
import * as TextAreaAll from 'react-textarea-autosize';
import { TinyLoader } from '../../../../primitives/loaders/tiny-loader';
import { Tooltip } from '../../../../primitives';
import { isTextSelected } from '../common/utils';
export var inlineTextAreaKey = 'inline-text-area';
var TextAreaComp = typeof TextAreaAll === 'object' ? TextAreaDefault : TextAreaAll;
var BORDER_HEIGHT = 2;
var DEFAULT_LINES = 4;
var SINGLE_LINE_HEIGHT = 20;
var MIN_HEIGHT = 40;
var keys = {};
var UnFocusedBody = function (props) {
    var _a;
    var isHovered = props.isHovered, placeholder = props.placeholder, maxRows = props.maxRows, textHeightRef = props.textHeightRef, value = props.value, ellipsis = props.ellipsis, disabled = props.disabled, outline = props.outline, customViewRenderer = props.customViewRenderer;
    var maybeHover = (isHovered && !props.disabled) ?
        React.createElement(Text, { color: 't60' }, placeholder) :
        React.createElement(Text, { color: 't50' }, '-');
    var style = (_a = {},
        _a['--var-rows'] = maxRows || DEFAULT_LINES,
        _a.maxHeight = 'max-content',
        _a.minHeight = MIN_HEIGHT,
        _a);
    var className = classNames('unfocused-text', { outline: outline });
    var innerContentClassName = classNames('inner-content', { ellipsis: ellipsis });
    var unfocusedValue = (React.createElement(Text, { className: className, ellipsis: ellipsis, relativeToBody: true },
        React.createElement("div", { className: innerContentClassName, ref: textHeightRef }, value || maybeHover)));
    var comp = (React.createElement("div", { id: 'unfocused-body', style: style, className: classNames('unfocused-body', { disabled: !!props.disabled }) }, unfocusedValue));
    if (disabled) {
        return (React.createElement(Tooltip, { relativeToBody: true, body: disabled }, comp));
    }
    else if (customViewRenderer) {
        return React.createElement("div", { className: 'custom-view' }, customViewRenderer(value));
    }
    else {
        return comp;
    }
};
export var InlineTextArea = function (props) {
    var value = props.value, texts = props.texts, onChange = props.onChange, maxRows = props.maxRows, className = props.className, shouldShowMore = props.shouldShowMore, ellipsis = props.ellipsis, hideClear = props.hideClear, customViewRenderer = props.customViewRenderer;
    var placeholder = texts.placeholder, clear = texts.clear, focusedPlaceholder = texts.focusedPlaceholder;
    var maybeDisabledPlaceholder = props.disabled ? '-' : placeholder;
    var _a = React.useState(false), isFocused = _a[0], setIsFocused = _a[1];
    var _b = React.useState(false), isHovered = _b[0], setIsHovered = _b[1];
    var _c = React.useState(false), isExpanded = _c[0], setIsExpanded = _c[1];
    var _d = React.useState(value), unconfirmedValue = _d[0], setUnconfirmedValue = _d[1];
    var _e = React.useState(0), textHeight = _e[0], setTextHeight = _e[1];
    var _f = React.useState(false), isLoading = _f[0], setIsLoading = _f[1];
    var MAX_BOX_HEIGHT = ((maxRows || DEFAULT_LINES) + 1) * SINGLE_LINE_HEIGHT + BORDER_HEIGHT;
    var textHeightRef = React.useRef(null);
    var expandExists = textHeight + SINGLE_LINE_HEIGHT > MAX_BOX_HEIGHT;
    var _g = React.useState(false), hasExpandOnFocus = _g[0], setHasExpandOnFocus = _g[1];
    React.useEffect(function () {
        setUnconfirmedValue(value);
    }, [value]);
    React.useLayoutEffect(function () {
        var _a;
        var currentTextRefHeight = (_a = textHeightRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
        if (!!textHeightRef.current && currentTextRefHeight) {
            setTextHeight(currentTextRefHeight);
        }
        else {
            setHasExpandOnFocus(expandExists);
            setTextHeight(0);
        }
    }, [isFocused, value]);
    var onChangeInput = function (e) {
        setUnconfirmedValue(e.target.value);
    };
    var onFocus = function () {
        if (!isTextSelected(window)) {
            setIsFocused(!props.disabled);
        }
    };
    var onLeaveFocus = function () {
        setIsLoading(false);
        setIsExpanded(false);
        setIsHovered(false);
        setIsFocused(false);
    };
    var onMouseOver = function () { return setIsHovered(true); };
    var onMouseLeave = function () { return setIsHovered(false); };
    var onConfirm = function (e) {
        e.preventDefault();
        var result = onChange(unconfirmedValue, e);
        if (isPromise(result)) {
            setIsLoading(true);
            result.then(onLeaveFocus, function () {
                onLeaveFocus();
                setUnconfirmedValue(value);
            });
        }
        else {
            onLeaveFocus();
        }
    };
    var onCancel = function (e) {
        e.preventDefault();
        setIsExpanded(false);
        setIsFocused(false);
        setIsHovered(false);
        setUnconfirmedValue(value);
    };
    var onClear = function (e) {
        e.preventDefault();
        setUnconfirmedValue('');
    };
    var onKeyPress = function (e) {
        e.nativeEvent.stopImmediatePropagation();
        if (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.escape || e.keyCode === keyCodes.shift) {
            keys[e.keyCode] = true;
            if (keys[keyCodes.enter] && keys[keyCodes.shift]) {
                return;
            }
            else if (keys[keyCodes.enter]) {
                onConfirm(e);
                return;
            }
            else if (keys[keyCodes.escape]) {
                onCancel(e);
                return;
            }
        }
        else {
            keys = {};
        }
    };
    var toggleExpand = function (e) {
        e.stopPropagation();
        e.preventDefault();
        setIsExpanded(!isExpanded);
    };
    var cn = namespacedClassnames(inlineTextAreaKey, className, {
        minimized: expandExists && !isExpanded,
        expanded: expandExists,
        'preserve-layout': props.preserveContentLayout,
        focused: isFocused
    });
    var resetSelectionRange = function (e) {
        e.target.setSelectionRange(unconfirmedValue.length, unconfirmedValue.length);
        e.target.scrollTo(0, 0);
    };
    var inlineControl = (React.createElement(InlineControl, { onClear: onClear, onCancel: onCancel, onConfirm: onConfirm, disabledClear: !unconfirmedValue, clearText: clear, hideClear: hideClear }));
    var focusedBodyClassName = classNames('focused-body', { 'has-expand': hasExpandOnFocus });
    var focusedBody = (React.createElement("div", { className: focusedBodyClassName },
        React.createElement("div", { className: 'textarea-container' },
            React.createElement(TextAreaComp, { id: 'inline-textarea-comp', value: unconfirmedValue, placeholder: focusedPlaceholder || placeholder, onChange: onChangeInput, onKeyDown: onKeyPress, autoFocus: true, onFocus: resetSelectionRange, disabled: isLoading, maxRows: maxRows }),
            isLoading ? React.createElement("div", { className: 'loader-wrapper' },
                React.createElement(TinyLoader, null)) : null),
        !isLoading ? inlineControl : null));
    var outline = !isExpanded && value && expandExists;
    var unfocusedBody = (React.createElement(UnFocusedBody, { value: isFocused ? unconfirmedValue : value, placeholder: maybeDisabledPlaceholder, textHeightRef: textHeightRef, maxRows: maxRows, isExpanded: isExpanded, isHovered: isHovered, disabled: props.disabled, ellipsis: ellipsis, outline: !!outline, customViewRenderer: customViewRenderer }));
    var expandButton = (shouldShowMore !== false && expandExists)
        ? (React.createElement("div", { className: 'expand-button' },
            React.createElement(Text, { onClick: toggleExpand, color: 't60', type: 'ts' }, isExpanded ? 'Show less' : 'Show more')))
        : null;
    var componentStyle = {
        minHeight: 'max-content',
        height: 'max-content',
    };
    var textAreaContent = props.preserveContentLayout ? (React.createElement(React.Fragment, null,
        unfocusedBody,
        isFocused && focusedBody)) : isFocused ? focusedBody : unfocusedBody;
    return (React.createElement("div", { className: cn, style: componentStyle, onClick: onFocus, onBlur: onConfirm, onMouseOver: onMouseOver, onMouseLeave: onMouseLeave },
        textAreaContent,
        expandButton));
};
//# sourceMappingURL=index.js.map